function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  let pendingArgs;
  const debounced = (...args) => {
    clearTimeout(t);
    pendingArgs = args;
    t = setTimeout(debounced.flush, wait);
  };
  // Run the pending call now, if there is one.
  debounced.flush = () => {
    clearTimeout(t);
    if (!pendingArgs) return;
    const args = pendingArgs;
    pendingArgs = undefined;
    fn.apply(this, args);
  };
  return debounced;
}


function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
    );
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
    window.removeEventListener('resize', this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    this.dataset.section = this.closest('.shopify-section').id.replace('shopify-section-', '');
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`)
    );
  }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }

      // Workaround for safari iframe bug
      const formerStyle = deferredElement.getAttribute('style');
      deferredElement.setAttribute('style', 'display: block;');
      window.setTimeout(() => {
        deferredElement.setAttribute('style', formerStyle);
      }, 0);
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      this.dispatchProductSelectEvent();

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  getAllSelectedOptions() {
    const options = [];
    this.querySelectorAll('fieldset, .product-form__input--dropdown').forEach((group) => {
      const checked = group.querySelector('input:checked') || group.querySelector('select option[selected]');
      if (checked) {
        options.push({ name: checked.dataset.optionName || '', value: checked.value });
      }
    });
    return options;
  }

  dispatchProductSelectEvent() {
    const { ProductSelectEvent } = window.StandardEvents || {};
    if (!ProductSelectEvent) return;

    const deferred = ProductSelectEvent.createPromise();
    this.pendingSelectPromise = deferred;

    this.dispatchEvent(
      new ProductSelectEvent({
        product: {
          id: this.dataset.productId,
          title: this.dataset.productTitle,
          handle: this.dataset.productHandle,
        },
        selectedOptions: this.getAllSelectedOptions(),
        promise: deferred.promise,
      })
    );
  }

  takePendingSelectPromise() {
    const deferred = this.pendingSelectPromise;
    this.pendingSelectPromise = null;
    return deferred;
  }

  resolvePendingSelectPromise(variant, sourceVariantSelects = this) {
    const deferred = this.takePendingSelectPromise();
    if (!deferred) return;

    if (variant) {
      deferred.resolve({
        variant: {
          id: variant.id,
          title: variant.title,
          availableForSale: variant.available,
          price: {
            amount: sourceVariantSelects?.dataset.selectedPriceAmount,
            currencyCode: sourceVariantSelects?.dataset.currencyCode,
          },
          selectedOptions: this.getAllSelectedOptions(),
        },
      });
    } else {
      deferred.resolve({ variant: null });
    }
  }

  rejectPendingSelectPromise(error) {
    this.takePendingSelectPromise()?.reject(error);
  }

  /** Keep swatch labels text-safe here and in product-variant-picker.liquid after section refreshes. */
  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.textContent = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}

customElements.define('variant-selects', VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
          this.remove();
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class BulkAdd extends HTMLElement {
  static ASYNC_REQUEST_DELAY = 250;
  static pendingCartUpdate = Promise.resolve();

  constructor() {
    super();
    this.queue = [];
    this.setRequestStarted(false);
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });

    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, BulkAdd.ASYNC_REQUEST_DELAY);
  }

  sendRequest(queue) {
    this.setRequestStarted(true);
    const items = {};

    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));

    return this.updateMultipleQty(items);
  }

  setRequestStarted(requestStarted) {
    this._requestStarted = requestStarted;
  }

  get requestStarted() {
    return this._requestStarted;
  }

  /**
   * Convert variant totals to line-key changes without counting discount-split units twice.
   * Bulk controls show one total across all variant lines, including selling plans and properties.
   * Preserve cart order: add increases to the first line and take decreases from lines in order.
   * @param {Object<string, number>} items Requested totals keyed by variant ID.
   * @param {Array<object>} cartItems The current AJAX cart lines.
   * @returns {Object<string, number>} Quantities keyed by line key, or variant ID for new items.
   */
  buildCartUpdates(items, cartItems) {
    const updates = {};
    for (const [variantId, quantity] of Object.entries(items)) {
      const lines = cartItems.filter((item) => String(item.variant_id) === variantId);
      if (!lines.length) {
        if (quantity > 0) updates[variantId] = quantity;
        continue;
      }

      const currentQuantity = lines.reduce((total, line) => total + line.quantity, 0);
      const delta = quantity - currentQuantity;
      if (delta > 0) {
        updates[lines[0].key] = lines[0].quantity + delta;
        continue;
      }

      let remaining = -delta;
      for (const line of lines) {
        if (remaining <= 0) break;
        const removed = Math.min(line.quantity, remaining);
        updates[line.key] = line.quantity - removed;
        remaining -= removed;
      }
    }
    return updates;
  }

  /**
   * Serialize bulk mutations across components and read fresh line keys before each write.
   * A queued edit can outlive its rendered row, and discounts can change line keys between writes.
   * @param {Object<string, number>} items Requested totals keyed by variant ID.
   * @param {string[]} sections Bundled section IDs to render with the cart mutation.
   * @param {string} sectionsUrl Page context for bundled section rendering.
   * @param {{removeProductId?: string}} [options] Remove this product's current lines instead of explicit targets.
   * @returns {Promise<object>} The updated AJAX cart.
   */
  updateCart(items, sections, sectionsUrl, { removeProductId } = {}) {
    const update = BulkAdd.pendingCartUpdate.then(async () => {
      const cartResponse = await fetch(`${routes.cart_url}.js`, { cache: 'no-store' });
      if (!cartResponse.ok) throw new Error(window.cartStrings.error);
      const cart = await cartResponse.json();
      const targets = removeProductId
        ? Object.fromEntries(
            cart.items
              .filter((item) => String(item.product_id) === String(removeProductId))
              .map((item) => [item.variant_id, 0])
          )
        : items;
      const updates = this.buildCartUpdates(targets, cart.items);
      const linesUpdate = this.startCartLinesUpdate(updates, cart.items);

      try {
        const response = await fetch(routes.cart_update_url, {
          ...fetchConfig(),
          body: JSON.stringify({ updates, sections, sections_url: sectionsUrl }),
        });
        const parsedState = await response.json();
        if (parsedState.errors || !response.ok) {
          throw Object.assign(new Error(parsedState.errors || window.cartStrings.error), { code: 'INVALID' });
        }
        linesUpdate?.resolve(parsedState);
        return parsedState;
      } catch (error) {
        linesUpdate?.reject(error);
        throw error;
      }
    });

    BulkAdd.pendingCartUpdate = update.catch(() => undefined);
    return update;
  }

  /**
   * Publish changes from the same cart snapshot used to build the mutation.
   * Keys absent from the snapshot are variant IDs for additions, not existing line keys.
   * @param {Object<string, number>} updates Line-key quantities, or variant-ID quantities for new items, from buildCartUpdates().
   * @param {Array<object>} cartItems The cart snapshot used to build updates.
   * @returns {object|undefined} Deferred event completion handlers when Standard Events are available.
   */
  startCartLinesUpdate(updates, cartItems) {
    const { CartLinesUpdateEvent } = window.StandardEvents || {};
    if (!CartLinesUpdateEvent) return;

    const linesByAction = Object.entries(updates).reduce((groups, [id, quantity]) => {
      const nextQuantity = parseInt(quantity, 10);
      const cartLine = cartItems.find((item) => item.key === id);
      const currentQuantity = cartLine?.quantity || 0;

      if (Number.isNaN(nextQuantity) || currentQuantity === nextQuantity) return groups;

      const action = currentQuantity === 0 ? 'add' : nextQuantity === 0 ? 'remove' : 'update';
      let line;
      if (action === 'add') {
        line = { merchandiseId: id, quantity: nextQuantity };
      } else {
        line = { id, quantity: nextQuantity };
      }

      if (!groups[action]) groups[action] = [];
      groups[action].push(line);
      return groups;
    }, {});

    const deferreds = Object.entries(linesByAction).map(([action, lines]) => {
      const deferred = CartLinesUpdateEvent.createPromise();
      this.dispatchEvent(
        new CartLinesUpdateEvent({
          action,
          context: 'product',
          lines,
          promise: deferred.promise,
        })
      );
      return deferred;
    });

    return {
      resolve: (parsedState) => {
        const payload = { cart: CartLinesUpdateEvent.createCartFromAjaxResponse(parsedState) };
        deferreds.forEach((deferred) => deferred.resolve(payload));
      },
      reject: (error) => {
        deferreds.forEach((deferred) => deferred.reject(error));
      },
    };
  }

  dispatchCartErrorEvent(message, code) {
    const { CartErrorEvent } = window.StandardEvents || {};
    if (!CartErrorEvent) return;

    this.dispatchEvent(new CartErrorEvent({ error: message, code }));
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      event.target.setAttribute('value', inputValue);
      this.startQueue(index, inputValue);
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}

class CartPerformance {
  static #metric_prefix = "cart-performance"

  static createStartingMarker(benchmarkName) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    return performance.mark(`${metricName}:start`);
  }

  static measureFromEvent(benchmarkName, event) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`, {
      startTime: event.timeStamp
    });

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }

  static measureFromMarker(benchmarkName, startMarker) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      startMarker.name,
      `${metricName}:end`
    );
  }

  static measure(benchmarkName, callback) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`);

    callback();

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }
}

;
(function(){
  if(window.__vunroAttributionPassthrough&&window.__vunroAttributionPassthrough.version==="2026-05-23")return;

  var VERSION="2026-05-23";
  var STORAGE_FIRST="vunro_attribution_first";
  var STORAGE_LAST="vunro_attribution_last";
  var STORAGE_CART_FINGERPRINT="vunro_attribution_cart_fingerprint";
  var STORAGE_TTL_MS=90*24*60*60*1000;
  var MAX_VALUE_LENGTH=512;
  var MAX_URL_LENGTH=1400;
  var CART_UPDATE_DEBOUNCE_MS=350;
  var TRACKING_PARAM_NAMES=[
    "utm_source",
    "utm_medium",
    "utm_campaign",
    "utm_id",
    "utm_term",
    "utm_content",
    "ad_name",
    "adset_name",
    "campaign_id",
    "adset_id",
    "ad_id",
    "omega_utm_source",
    "omega_utm_campaign",
    "omega_ad_name",
    "omega_adset_name",
    "fbclid",
    "gclid",
    "_fbc",
    "_fbp",
    "fbc",
    "fbp"
  ];
  var SHOPPING_PATH_PREFIXES=[
    "/products/",
    "/collections/",
    "/cart",
    "/checkout"
  ];
  var CART_ATTRIBUTE_MAP={
    utm_source:"_vunro_utm_source",
    utm_medium:"_vunro_utm_medium",
    utm_campaign:"_vunro_utm_campaign",
    utm_id:"_vunro_utm_id",
    utm_term:"_vunro_utm_term",
    utm_content:"_vunro_utm_content",
    ad_name:"_vunro_ad_name",
    adset_name:"_vunro_adset_name",
    campaign_id:"_vunro_campaign_id",
    adset_id:"_vunro_adset_id",
    ad_id:"_vunro_ad_id",
    omega_utm_source:"_vunro_omega_utm_source",
    omega_utm_campaign:"_vunro_omega_utm_campaign",
    omega_ad_name:"_vunro_omega_ad_name",
    omega_adset_name:"_vunro_omega_adset_name",
    fbclid:"_vunro_fbclid",
    gclid:"_vunro_gclid",
    _fbc:"_vunro_fbc",
    _fbp:"_vunro_fbp",
    fbc:"_vunro_fbc",
    fbp:"_vunro_fbp"
  };

  var cartUpdateTimer=null;

  function safeNow(){
    return Date.now?Date.now():(new Date()).getTime();
  }

  function truncate(value,maxLength){
    var text=String(value||"").trim();
    if(!text)return "";
    return text.length>maxLength?text.slice(0,maxLength):text;
  }

  function storageGet(key){
    try{
      var raw=window.localStorage&&window.localStorage.getItem(key);
      if(!raw&&window.sessionStorage)raw=window.sessionStorage.getItem(key);
      if(!raw)return null;
      var parsed=JSON.parse(raw);
      if(!parsed||typeof parsed!=="object")return null;
      if(parsed.captured_at&&safeNow()-Number(parsed.captured_at)>STORAGE_TTL_MS)return null;
      return parsed;
    }catch(error){
      return null;
    }
  }

  function storageSet(key,value){
    var raw=JSON.stringify(value);
    try{
      if(window.localStorage)window.localStorage.setItem(key,raw);
    }catch(error){}
    try{
      if(window.sessionStorage)window.sessionStorage.setItem(key,raw);
    }catch(error){}
  }

  function trackingParamsFromUrl(urlValue){
    var output={};
    try{
      var parsed=new URL(urlValue,window.location.href);
      TRACKING_PARAM_NAMES.forEach(function(name){
        var value=parsed.searchParams.get(name);
        if(value!==null&&String(value).trim()!==""){
          output[name]=truncate(value,MAX_VALUE_LENGTH);
        }
      });
    }catch(error){}
    return output;
  }

  function hasKeys(object){
    return !!object&&Object.keys(object).length>0;
  }

  function currentSnapshot(){
    var params=trackingParamsFromUrl(window.location.href);
    if(!hasKeys(params))return null;
    return {
      params:params,
      landing_url:truncate(window.location.href,MAX_URL_LENGTH),
      referrer:truncate(document.referrer||"",MAX_URL_LENGTH),
      captured_at:safeNow(),
      version:VERSION
    };
  }

  function captureAttribution(){
    var snapshot=currentSnapshot();
    if(!snapshot)return false;
    var first=storageGet(STORAGE_FIRST);
    if(!first||!hasKeys(first.params)){
      storageSet(STORAGE_FIRST,snapshot);
    }
    storageSet(STORAGE_LAST,snapshot);
    return true;
  }

  function latestAttribution(){
    var last=storageGet(STORAGE_LAST);
    var first=storageGet(STORAGE_FIRST);
    return {
      first:first&&hasKeys(first.params)?first:null,
      last:last&&hasKeys(last.params)?last:null
    };
  }

  function isSameOriginUrl(parsed){
    return parsed.origin===window.location.origin;
  }

  function isShoppingPath(pathname){
    var path=String(pathname||"/");
    return SHOPPING_PATH_PREFIXES.some(function(prefix){
      return path===prefix||path.indexOf(prefix)===0;
    });
  }

  function shouldDecorateHref(href){
    if(!href||href.charAt(0)==="#"||href.indexOf("mailto:")===0||href.indexOf("tel:")===0||href.indexOf("javascript:")===0)return false;
    try{
      var parsed=new URL(href,window.location.href);
      return isSameOriginUrl(parsed)&&isShoppingPath(parsed.pathname);
    }catch(error){
      return false;
    }
  }

  function decorateUrl(href){
    var attribution=latestAttribution();
    var source=attribution.last||attribution.first;
    if(!source||!hasKeys(source.params))return href;
    try{
      var parsed=new URL(href,window.location.href);
      if(!isSameOriginUrl(parsed)||!isShoppingPath(parsed.pathname))return href;
      Object.keys(source.params).forEach(function(name){
        if(!parsed.searchParams.has(name)){
          parsed.searchParams.set(name,source.params[name]);
        }
      });
      return parsed.href;
    }catch(error){
      return href;
    }
  }

  function decorateAnchor(anchor){
    if(!anchor||!anchor.getAttribute)return;
    var rawHref=anchor.getAttribute("href");
    if(!shouldDecorateHref(rawHref))return;
    var decorated=decorateUrl(rawHref);
    if(decorated&&decorated!==anchor.href){
      anchor.href=decorated;
      try{anchor.dataset.vunroAttributionPassthrough="1";}catch(error){}
    }
  }

  function decorateAnchors(root){
    if(!root||!root.querySelectorAll)return;
    var anchors=root.querySelectorAll('a[href]');
    for(var i=0;i<anchors.length;i+=1){
      decorateAnchor(anchors[i]);
    }
  }

  function addPrefixedAttributes(output,prefix,snapshot){
    if(!snapshot||!hasKeys(snapshot.params))return;
    Object.keys(snapshot.params).forEach(function(name){
      var mapped=CART_ATTRIBUTE_MAP[name];
      if(!mapped)return;
      output[prefix+mapped.replace("_vunro_","")]=snapshot.params[name];
    });
    if(snapshot.landing_url)output[prefix+"landing_url"]=truncate(snapshot.landing_url,MAX_URL_LENGTH);
    if(snapshot.referrer)output[prefix+"referrer"]=truncate(snapshot.referrer,MAX_URL_LENGTH);
    output[prefix+"captured_at"]=String(snapshot.captured_at||"");
  }

  function buildCartAttributes(){
    var attribution=latestAttribution();
    var source=attribution.last||attribution.first;
    if(!source||!hasKeys(source.params))return null;
    var attrs={};
    Object.keys(source.params).forEach(function(name){
      var mapped=CART_ATTRIBUTE_MAP[name];
      if(mapped)attrs[mapped]=source.params[name];
    });
    attrs._vunro_landing_url=truncate((attribution.first||source).landing_url||source.landing_url||"",MAX_URL_LENGTH);
    attrs._vunro_referrer=truncate((attribution.first||source).referrer||source.referrer||"",MAX_URL_LENGTH);
    addPrefixedAttributes(attrs,"_vunro_first_",attribution.first);
    addPrefixedAttributes(attrs,"_vunro_last_",source);
    attrs._vunro_attribution_version=VERSION;
    return attrs;
  }

  function cartUpdateUrl(){
    try{
      var root=(window.Shopify&&window.Shopify.routes&&window.Shopify.routes.root)||"/";
      return new URL("cart/update.js",new URL(root,window.location.origin)).href;
    }catch(error){
      return "/cart/update.js";
    }
  }

  function attributesFingerprint(attributes){
    return JSON.stringify(attributes||{},Object.keys(attributes||{}).sort());
  }

  function sendCartAttributes(){
    var attributes=buildCartAttributes();
    if(!attributes)return Promise.resolve(false);
    var fingerprint=attributesFingerprint(attributes);
    var previous="";
    try{previous=window.sessionStorage&&window.sessionStorage.getItem(STORAGE_CART_FINGERPRINT)||"";}catch(error){}
    if(previous===fingerprint)return Promise.resolve(false);
    try{if(window.sessionStorage)window.sessionStorage.setItem(STORAGE_CART_FINGERPRINT,fingerprint);}catch(error){}
    return fetch(cartUpdateUrl(),{
      method:"POST",
      credentials:"same-origin",
      keepalive:true,
      headers:{
        "Content-Type":"application/json",
        "Accept":"application/json"
      },
      body:JSON.stringify({attributes:attributes})
    }).then(function(response){
      if(!response||!response.ok)throw new Error("cart attribution update failed");
      return true;
    }).catch(function(error){
      try{if(window.sessionStorage)window.sessionStorage.removeItem(STORAGE_CART_FINGERPRINT);}catch(storageError){}
      return false;
    });
  }

  function scheduleCartAttributeUpdate(){
    if(cartUpdateTimer)clearTimeout(cartUpdateTimer);
    cartUpdateTimer=setTimeout(function(){
      cartUpdateTimer=null;
      void sendCartAttributes();
    },CART_UPDATE_DEBOUNCE_MS);
  }

  function installMutationObserver(){
    if(!window.MutationObserver)return;
    try{
      var observer=new MutationObserver(function(records){
        records.forEach(function(record){
          for(var i=0;i<(record.addedNodes||[]).length;i+=1){
            var node=record.addedNodes[i];
            if(node&&node.nodeType===1){
              if(node.matches&&node.matches("a[href]"))decorateAnchor(node);
              decorateAnchors(node);
            }
          }
        });
      });
      observer.observe(document.documentElement||document.body,{childList:true,subtree:true});
    }catch(error){}
  }

  function installClickGuard(){
    document.addEventListener("click",function(event){
      var anchor=event.target&&event.target.closest?event.target.closest("a[href]"):null;
      if(anchor)decorateAnchor(anchor);
      var checkoutTrigger=event.target&&event.target.closest
        ?event.target.closest('a[href*="/checkout"], button[name="checkout"], input[name="checkout"], [data-checkout], [data-vunro-checkout]')
        :null;
      if(checkoutTrigger)void sendCartAttributes();
    },true);
    document.addEventListener("submit",function(event){
      var form=event.target;
      if(form&&form.matches&&form.matches('form[action*="/cart"], form[action*="/checkout"]')){
        void sendCartAttributes();
      }
    },true);
  }

  function refresh(){
    captureAttribution();
    decorateAnchors(document);
    scheduleCartAttributeUpdate();
  }

  window.__vunroAttributionPassthrough={
    version:VERSION,
    refresh:refresh,
    decorateUrl:decorateUrl,
    buildCartAttributes:buildCartAttributes,
    sendCartAttributes:sendCartAttributes
  };

  if(document.readyState==="loading"){
    document.addEventListener("DOMContentLoaded",refresh,{once:true});
  }else{
    refresh();
  }
  installMutationObserver();
  installClickGuard();
})();
(function(){
  function isVunroLandingRuntimeSrc(value){
    return typeof value==="string"&&(
      value.indexOf("integrations.getvunro.com/plasmic/")!==-1||
      value.indexOf("integrations.getvunro.com/vunro-pages/")!==-1
    );
  }

  function isEmbeddedLandingPage(){
    try{
      var supportedPathnames=["/pages/buy-blue-steel-blades","/pages/buy-master-grooming-bundle"];
      if(supportedPathnames.indexOf(window.location.pathname)===-1)return!1;
      var iframe=document.querySelector('.vunro-landing-frame iframe');
      return!!(iframe&&isVunroLandingRuntimeSrc(iframe.getAttribute("src")||""));
    }catch{
      return!1;
    }
  }

  window.__vunroIsEmbeddedLandingPage=isEmbeddedLandingPage;
})();

(function(){
  if(window.__vunroCartTrace)return;

  function traceEnabled(){
    try{
      if(window.location.search.indexOf("vunro_cart_trace=1")!==-1)return!0;
      if(window.localStorage&&window.localStorage.getItem("vunro_cart_trace")==="1")return!0;
    }catch{}
    return!1;
  }

  function sanitizeDetail(value,depth){
    if(depth>3)return"[max-depth]";
    if(value===null||typeof value==="undefined")return value;
    var valueType=typeof value;
    if(valueType==="string"||valueType==="number"||valueType==="boolean")return value;
    if(Array.isArray(value))return value.slice(0,12).map(function(item){return sanitizeDetail(item,depth+1);});
    if(valueType!=="object")return String(value);
    var summary={};
    Object.keys(value).slice(0,16).forEach(function(key){
      try{
        summary[key]=sanitizeDetail(value[key],depth+1);
      }catch(error){
        summary[key]="[unavailable]";
      }
    });
    return summary;
  }

  function pushTrace(type,detail){
    if(!traceEnabled())return;
    var store=window.__vunroCartTraceStore;
    if(!Array.isArray(store)){
      store=[];
      window.__vunroCartTraceStore=store;
    }
    store.push({
      time:Date.now(),
      type:type,
      detail:sanitizeDetail(detail||{},0)
    });
    if(store.length>400)store.splice(0,store.length-400);
  }

  window.__vunroCartTrace={
    enabled:traceEnabled,
    push:pushTrace,
    read:function(){
      var store=window.__vunroCartTraceStore;
      return Array.isArray(store)?store.slice():[];
    },
    clear:function(){
      window.__vunroCartTraceStore=[];
    }
  };

  pushTrace("trace-ready",{path:window.location.pathname});
})();

(function(){
  if(window.__vunroCartTracePagefly)return;
  window.__vunroCartTracePagefly=!0;

  function trace(type,detail){
    try{
      window.__vunroCartTrace&&window.__vunroCartTrace.push(type,detail);
    }catch{}
  }

  function callbackLabel(callback){
    if(typeof callback!=="function")return"non-function";
    try{
      var source=Function.prototype.toString.call(callback);
      if(source.indexOf("cartDrawer.renderContents")!==-1)return"cartDrawer.renderContents";
      if(source.indexOf("_pf_handleFreeShopifyTheme")!==-1)return"_pf_handleFreeShopifyTheme";
      if(source.indexOf("window.__pagefly_theme_atc_check__")!==-1)return"__pagefly_theme_atc_check__";
      return(source.slice(0,120)||"anonymous").replace(/\s+/g," ");
    }catch{
      return"uninspectable";
    }
  }

  function patchStore(store){
    if(!store||typeof store.subscribe!=="function")return!1;
    if(store.__vunroCartTraceSubscribePatched)return!0;
    var originalSubscribe=store.subscribe;
    store.__vunroCartTraceSubscribePatched=!0;
    store.subscribe=function(callback){
      var label=callbackLabel(callback);
      trace("pagefly-subscribe",{label:label});
      if(typeof callback!=="function")return originalSubscribe.apply(this,arguments);
      return originalSubscribe.call(this,function(){
        trace("pagefly-callback",{label:label});
        return callback.apply(this,arguments);
      });
    };
    trace("pagefly-store-patched",{ok:!0});
    return!0;
  }

  var attempts=0;
  var timer=setInterval(function(){
    attempts+=1;
    if(patchStore(window.__pagefly_helper_store__)||attempts>200)clearInterval(timer);
  },50);
})();

(function(){
  if(window.__vunroLandingNativeIframeFallback)return;
  window.__vunroLandingNativeIframeFallback=!0;
  var allowedOrigin="https://integrations.getvunro.com";

  function mountFallback(){
    try{
      var supportedPathnames=["/pages/buy-blue-steel-blades","/pages/buy-master-grooming-bundle"];
      if(supportedPathnames.indexOf(window.location.pathname)===-1)return!1;
      var existingFrame=document.querySelector('.vunro-landing-frame iframe');
      if(existingFrame&&isVunroLandingRuntimeSrc(existingFrame.getAttribute("src")||""))return!0;
      var host=document.querySelector(".vunro-blue-steel-native-host");
      if(!host)return!1;

      var frameRoot=document.createElement("div");
      frameRoot.className="vunro-landing-frame vunro-blue-steel-native-fallback";
      frameRoot.id=(host.id||"vunro-blue-steel-native")+"-fallback";

      var iframe=document.createElement("iframe");
      iframe.src="https://integrations.getvunro.com/vunro-pages/";
      iframe.style.cssText="width:100%;min-height:0;height:3200px;border:0;display:block;";
      iframe.loading="eager";
      iframe.setAttribute("allowfullscreen","");

      frameRoot.appendChild(iframe);
      host.replaceWith(frameRoot);

      window.addEventListener("message",function(event){
        if(event.origin!==allowedOrigin||!event.data||event.data.type!=="vunro:landing-height")return;
        var numeric=Number(event.data.height);
        if(!Number.isFinite(numeric)||numeric<=0)return;
        iframe.style.height=Math.ceil(numeric)+"px";
      });

      iframe.addEventListener("load",function(){
        try{
          iframe.contentWindow&&iframe.contentWindow.postMessage({type:"vunro:landing-request-height"},allowedOrigin);
        }catch{}
      });
      return!0;
    }catch(error){
      console.error("VUNRO landing native iframe fallback failed:",error);
      return!1;
    }
  }

  var attempts=0;
  var timer=setInterval(function(){
    attempts+=1;
    if(mountFallback()||attempts>200)clearInterval(timer);
  },50);
})();

(function(){
  if(window.__vunroLandingCartUpdateSuppressor)return;
  window.__vunroLandingCartUpdateSuppressor=!0;
  if(typeof window.__vunroIsEmbeddedLandingPage!=="function"||!window.__vunroIsEmbeddedLandingPage()||typeof window.publish!=="function")return;
  var originalPublish=window.publish;
  window.publish=function(eventName,payload){
    try{
      window.__vunroCartTrace&&window.__vunroCartTrace.push("publish",{eventName:eventName,source:payload&&payload.source?payload.source:""});
      if(window.PUB_SUB_EVENTS&&eventName===window.PUB_SUB_EVENTS.cartUpdate&&payload&&payload.source==="vunro-landing-frame")return;
    }catch{}
    return originalPublish.apply(this,arguments);
  };
})();

(function(){
  if(window.__vunroLandingPageflyCartSubscriberBlocker)return;
  window.__vunroLandingPageflyCartSubscriberBlocker=!0;
  if(typeof window.__vunroIsEmbeddedLandingPage!=="function"||!window.__vunroIsEmbeddedLandingPage())return;

  function shouldBlockCallback(callback){
    if(typeof callback!=="function")return!1;
    var source="";
    try{
      source=Function.prototype.toString.call(callback);
    }catch{}
    return!!source&&(source.indexOf("cartDrawer.renderContents")!==-1||source.indexOf("_pf_handleFreeShopifyTheme")!==-1||source.indexOf("cart-notification-product,cart-notification-button,cart-drawer,cart-icon-bubble")!==-1||source.indexOf("window.__pagefly_theme_atc_check__")!==-1);
  }

  function patchStore(store){
    if(!store||typeof store.subscribe!=="function")return!1;
    if(store.__vunroLandingPageflySubscribePatched)return!0;
    var originalSubscribe=store.subscribe;
    store.__vunroLandingPageflySubscribePatched=!0;
    store.subscribe=function(callback){
      if(!shouldBlockCallback(callback))return originalSubscribe.apply(this,arguments);
      return originalSubscribe.call(this,function(){});
    };
    return!0;
  }

  var attempts=0;
  var timer=setInterval(function(){
    attempts+=1;
    if(patchStore(window.__pagefly_helper_store__)||attempts>200)clearInterval(timer);
  },50);
})();

(function(){
  if(window.__vunroLandingCartRenderDeduper)return;
  window.__vunroLandingCartRenderDeduper=!0;

  function buildSignature(tagName,parsedState){
    var sections=parsedState&&parsedState.sections?parsedState.sections:{};
    var signature={
      tagName:tagName,
      id:parsedState&&parsedState.id?String(parsedState.id):"",
      key:parsedState&&parsedState.key?String(parsedState.key):"",
      itemCount:parsedState&&typeof parsedState.item_count<"u"?String(parsedState.item_count):"",
      sections:Object.keys(sections).sort().map(function(name){
        var value=sections[name]||"";
        return[name,String(value).length];
      })
    };
    return JSON.stringify(signature);
  }

  function getSectionIds(instance){
    if(!instance||typeof instance.getSectionsToRender!=="function")return[];
    try{
      return instance.getSectionsToRender().map(function(section){
        return section&&section.id?section.id:"";
      }).filter(Boolean);
    }catch{
      return[];
    }
  }

  function getRobustCartSectionIds(instance){
    var ids=getSectionIds(instance).slice();
    var tagName=instance&&instance.tagName?String(instance.tagName).toLowerCase():"";
    var fallbackIds=tagName==="cart-notification"
      ? ["cart-notification-product","cart-notification-button","cart-icon-bubble"]
      : ["cart-drawer","cart-icon-bubble","cart-live-region-text","cart-drawer-items"];
    fallbackIds.forEach(function(sectionId){
      if(sectionId&&ids.indexOf(sectionId)===-1)ids.push(sectionId);
    });
    return ids;
  }

  function getMissingSectionIds(instance,parsedState){
    var sections=parsedState&&parsedState.sections?parsedState.sections:{};
    return getRobustCartSectionIds(instance).filter(function(sectionId){
      return!sections[sectionId];
    });
  }

  function buildSectionsRenderUrl(sectionIds){
    var ids=Array.isArray(sectionIds)?sectionIds.filter(Boolean):[];
    if(!ids.length)return"";
    var url=new URL(((window.Shopify&&window.Shopify.routes&&window.Shopify.routes.root)||"/"),window.location.origin);
    url.searchParams.set("sections",ids.join(","));
    return url.pathname+url.search;
  }

  async function fetchSections(sectionIds){
    var ids=Array.isArray(sectionIds)?sectionIds.filter(Boolean):[];
    if(!ids.length)return{};
    var response=await fetch(buildSectionsRenderUrl(ids),{
      method:"GET",
      headers:{"X-Requested-With":"XMLHttpRequest",Accept:"application/json"}
    });
    if(!response.ok)throw new Error("Cart section fetch failed with status "+response.status);
    var payload=await response.json();
    return payload&&typeof payload==="object"?payload:{};
  }

  async function ensureRenderableCartState(instance,parsedState){
    var state=parsedState&&typeof parsedState==="object"?parsedState:{};
    var currentSections=state.sections&&typeof state.sections==="object"?state.sections:{};
    var missingSectionIds=getMissingSectionIds(instance,state);
    if(!missingSectionIds.length)return state;
    var fetchedSections=await fetchSections(missingSectionIds);
    return Object.assign({},state,{sections:Object.assign({},fetchedSections,currentSections)});
  }

  function ensureDrawerOpen(instance,tagName){
    if(tagName!=="cart-drawer"||!instance||typeof instance.open!=="function")return;
    setTimeout(function(){
      try{
        instance.classList.contains("active")||instance.open();
      }catch{}
    },0);
  }

  function wrapRender(tagName){
    var ctor=window.customElements&&window.customElements.get(tagName);
    if(!ctor||!ctor.prototype||typeof ctor.prototype.renderContents!=="function")return!1;
    if(ctor.prototype.__vunroLandingRenderDeduperPatched)return!0;
    var originalRender=ctor.prototype.renderContents;
    ctor.prototype.__vunroLandingRenderDeduperPatched=!0;
    ctor.prototype.renderContents=async function(parsedState){
      try{
        var signature=buildSignature(tagName,parsedState||{});
        var now=Date.now();
        if(this.__vunroLastRenderSignature===signature&&this.__vunroLastRenderAt&&now-this.__vunroLastRenderAt<900){
          try{
            window.__vunroCartTrace&&window.__vunroCartTrace.push("render-deduped",{
              tagName:tagName,
              itemCount:parsedState&&typeof parsedState.item_count<"u"?parsedState.item_count:null,
              sectionKeys:Object.keys(parsedState&&parsedState.sections&&typeof parsedState.sections==="object"?parsedState.sections:{}).sort()
            });
          }catch{}
          return;
        }
        this.__vunroLastRenderSignature=signature;
        this.__vunroLastRenderAt=now;
      }catch{}
      try{
        window.__vunroCartTrace&&window.__vunroCartTrace.push("render-start",{
          tagName:tagName,
          itemCount:parsedState&&typeof parsedState.item_count<"u"?parsedState.item_count:null,
          sectionKeys:Object.keys(parsedState&&parsedState.sections&&typeof parsedState.sections==="object"?parsedState.sections:{}).sort()
        });
      }catch{}

      var nextArgs=Array.prototype.slice.call(arguments);
      var nextState=parsedState;
      try{
        nextState=await ensureRenderableCartState(this,parsedState||{});
        if(nextState!==parsedState){
          nextArgs[0]=nextState;
        }
      }catch{}

      try{
        var result=await originalRender.apply(this,nextArgs);
        try{
          window.__vunroCartTrace&&window.__vunroCartTrace.push("render-success",{tagName:tagName});
        }catch{}
        try{
          window.__vunroLastDrawerRenderAt=Date.now();
        }catch{}
        ensureDrawerOpen(this,tagName);
        return result;
      }catch(renderError){
        try{
          window.__vunroCartTrace&&window.__vunroCartTrace.push("render-error",{tagName:tagName,message:renderError&&renderError.message?renderError.message:String(renderError)});
        }catch{}
        var sectionIds=getSectionIds(this);
        if(!sectionIds.length)throw renderError;
        var fallbackSections=await fetchSections(sectionIds);
        var fallbackState=Object.assign({},nextState||parsedState||{},{sections:fallbackSections});
        nextArgs[0]=fallbackState;
        var fallbackResult=await originalRender.apply(this,nextArgs);
        ensureDrawerOpen(this,tagName);
        return fallbackResult;
      }
    };
    return!0;
  }

  var attempts=0;
  var timer=setInterval(function(){
    attempts+=1;
    var drawerReady=wrapRender("cart-drawer");
    var notificationReady=wrapRender("cart-notification");
    if((drawerReady&&notificationReady)||attempts>200)clearInterval(timer);
  },50);
})();

(function(){
  if(window.__vunroDesktopSelleasyItemsPlacement)return;
  window.__vunroDesktopSelleasyItemsPlacement=!0;

  function shouldUseDesktopItemsPlacement(){
    try{
      return !!window.matchMedia&&window.matchMedia("(min-width: 750px)").matches;
    }catch{
      return!1;
    }
  }

  function rehomeSelleasy(){
    if(!shouldUseDesktopItemsPlacement())return;
    var drawer=document.querySelector("cart-drawer");
    if(!drawer)return;
    var items=drawer.querySelector("cart-drawer-items");
    var selleasy=drawer.querySelector("lb-upsell-widget");
    if(!items||!selleasy||selleasy.parentNode===items)return;
    items.appendChild(selleasy);
    try{
      window.__vunroCartTrace&&window.__vunroCartTrace.push("desktop-selleasy-rehomed",{
        parentTag:"cart-drawer-items"
      });
    }catch{}
  }

  var placementPending=false;
  function schedulePlacement(){
    if(placementPending)return;
    placementPending=true;
    requestAnimationFrame(function(){placementPending=false;rehomeSelleasy();});
  }
  var placementDrawer=document.querySelector("cart-drawer");
  if(placementDrawer){
    new MutationObserver(schedulePlacement).observe(placementDrawer,{childList:true,subtree:true});
    schedulePlacement();
  }
  window.addEventListener("resize",schedulePlacement);

})();

(function(){
  if(window.__vunroDesktopSelleasyVisualStabilizer)return;
  window.__vunroDesktopSelleasyVisualStabilizer=!0;

  function shouldUseDesktopItemsPlacement(){
    try{
      return !!window.matchMedia&&window.matchMedia("(min-width: 750px)").matches;
    }catch{
      return!1;
    }
  }

  function ensureStyles(){
    if(document.getElementById("vunro-desktop-selleasy-stabilizer-styles"))return;
    var style=document.createElement("style");
    style.id="vunro-desktop-selleasy-stabilizer-styles";
    style.textContent="" +
      "cart-drawer-items lb-upsell-widget{transition:opacity .18s ease,min-height .18s ease;}" +
      "cart-drawer-items lb-upsell-widget[data-vunro-stabilizing='1']{opacity:.01;pointer-events:none;}" +
      "cart-drawer-items lb-upsell-widget[data-vunro-ready='1']{opacity:1;}";
    document.head.appendChild(style);
  }

  function getWidgetState(widget){
    if(!widget)return null;
    return{
      height:Math.round(widget.getBoundingClientRect().height||0),
      htmlLen:(widget.innerHTML||"").length
    };
  }

  function isStableState(state){
    return !!state&&state.height>=120&&state.htmlLen>=1000;
  }

  function isRebuildingState(state){
    return !!state&&(state.height<=24||state.htmlLen<=200);
  }

  function getRememberedStableHeight(){
    try{
      var stored=Number(window.sessionStorage&&window.sessionStorage.getItem("vunroDesktopSelleasyStableHeight")||0);
      return stored>0?stored:0;
    }catch{
      return 0;
    }
  }

  function rememberStableHeight(height){
    try{
      if(height>0&&window.sessionStorage)window.sessionStorage.setItem("vunroDesktopSelleasyStableHeight",String(height));
    }catch{}
  }

  function getRememberedStableTriggerId(){
    try{
      return String(window.sessionStorage&&window.sessionStorage.getItem("vunroDesktopSelleasyStableTriggerId")||"");
    }catch{
      return"";
    }
  }

  function rememberStableTriggerId(triggerId){
    try{
      if(triggerId&&window.sessionStorage)window.sessionStorage.setItem("vunroDesktopSelleasyStableTriggerId",String(triggerId));
    }catch{}
  }

  function stabilizeWidget(){
    if(!shouldUseDesktopItemsPlacement())return;
    ensureStyles();
    var widget=document.querySelector("cart-drawer-items lb-upsell-widget");
    if(!widget)return;
    var state=getWidgetState(widget);
    var now=Date.now();
    var lastStableHeight=Number(widget.dataset.vunroLastStableHeight||0)||getRememberedStableHeight();
    var currentTriggerId=String(widget.getAttribute("lb-trigger-product-id")||"");
    var rememberedTriggerId=String(widget.dataset.vunroLastStableTriggerId||"")||getRememberedStableTriggerId();
    var lastAddedProductId=String(window.__vunroLastAddedProductId||"");
    var mutationRecent=!!(window.__vunroCartMutationAt&&now-window.__vunroCartMutationAt<2200);

    if(isStableState(state)){
      if(mutationRecent&&lastAddedProductId&&currentTriggerId&&rememberedTriggerId&&currentTriggerId===rememberedTriggerId&&lastAddedProductId!==currentTriggerId){
        widget.style.minHeight=(lastStableHeight||391)+"px";
        widget.dataset.vunroStabilizing="1";
        widget.dataset.vunroReady="0";
        try{
          window.__vunroCartTrace&&window.__vunroCartTrace.push("selleasy-visual-waiting-product-context",{currentTriggerId:currentTriggerId,lastAddedProductId:lastAddedProductId,minHeight:lastStableHeight||391});
        }catch{}
        return;
      }
      widget.dataset.vunroLastStableHeight=String(state.height);
      rememberStableHeight(state.height);
      if(currentTriggerId){
        widget.dataset.vunroLastStableTriggerId=currentTriggerId;
        rememberStableTriggerId(currentTriggerId);
      }
      widget.dataset.vunroReady="1";
      widget.dataset.vunroStabilizing="0";
      if(widget.__vunroReleaseTimer)clearTimeout(widget.__vunroReleaseTimer);
      widget.__vunroReleaseTimer=setTimeout(function(){
        if(widget.dataset.vunroStabilizing==="1")return;
        widget.style.minHeight="";
      },260);
      try{
        window.__vunroCartTrace&&window.__vunroCartTrace.push("selleasy-visual-stable",{height:state.height,htmlLen:state.htmlLen});
      }catch{}
      return;
    }

    if(!lastStableHeight&&state&&state.htmlLen>0){
      lastStableHeight=391;
      widget.dataset.vunroLastStableHeight=String(lastStableHeight);
      try{
        window.__vunroCartTrace&&window.__vunroCartTrace.push("selleasy-visual-cold-start",{height:state.height,htmlLen:state.htmlLen,minHeight:lastStableHeight});
      }catch{}
    }

    if(lastStableHeight&&isRebuildingState(state)){
      widget.style.minHeight=lastStableHeight+"px";
      widget.dataset.vunroStabilizing="1";
      widget.dataset.vunroReady="0";
      try{
        window.__vunroCartTrace&&window.__vunroCartTrace.push("selleasy-visual-stabilizing",{height:state.height,htmlLen:state.htmlLen,minHeight:lastStableHeight});
      }catch{}
    }
  }

  var visualPending=false;
  var contextSettleTimer;
  var visualDrawer=document.querySelector("cart-drawer");
  function scheduleVisual(){
    if(!visualDrawer||!visualDrawer.classList.contains("active")||visualPending)return;
    visualPending=true;
    requestAnimationFrame(function(){visualPending=false;stabilizeWidget();});
    if(window.__vunroCartMutationAt&&Date.now()-window.__vunroCartMutationAt<2200){
      clearTimeout(contextSettleTimer);
      contextSettleTimer=setTimeout(scheduleVisual,2250);
    }
  }
  if(visualDrawer){
    new MutationObserver(function(records){
      if(records.some(function(record){
        if(record.target===visualDrawer)return record.attributeName==='class';
        if(record.type==='childList')return true;
        return record.target.closest&&record.target.closest('lb-upsell-widget')&&record.target.tagName!=='LB-UPSELL-WIDGET';
      }))scheduleVisual();
    }).observe(visualDrawer,{childList:true,subtree:true,attributes:true,attributeFilter:['class','style']});
    scheduleVisual();
  }
  window.addEventListener("resize",scheduleVisual);

})();

(function(){
  if(window.__vunroLandingSelleasyDrawerRefreshGuard)return;
  window.__vunroLandingSelleasyDrawerRefreshGuard=!0;

  function markMutation(urlString){
    try{
      if(urlString.indexOf("selleasy")!==-1)return;
      if(urlString.indexOf("/cart/add")!==-1||urlString.indexOf("/cart/change")!==-1||urlString.indexOf("/cart/update")!==-1||urlString.indexOf("/cart/clear")!==-1){
        window.__vunroCartMutationAt=Date.now();
      }
    }catch{}
  }

  function scheduleDrawerOpen(urlString,response){
    try{
      if(urlString.indexOf("/cart/add")===-1||!response||!response.ok)return;
      setTimeout(function(){
        try{
          var drawer=document.querySelector("cart-drawer");
          drawer&&typeof drawer.open==="function"&&!drawer.classList.contains("active")&&!drawer.classList.contains("is-empty")&&drawer.open();
        }catch{}
      },250);
    }catch{}
  }

  function shouldSuppressSelleasyCartUpdate(urlString){
    try{
      if(!urlString||urlString.indexOf("/cart/update")===-1||urlString.indexOf("seel=1")===-1)return!1;
      var now=Date.now();
      var lastMutationAt=window.__vunroCartMutationAt||0;
      var lastDrawerRenderAt=window.__vunroLastDrawerRenderAt||0;
      var widgetPresent=!!document.querySelector("cart-drawer lb-upsell-widget");
      return!!(widgetPresent&&lastMutationAt&&lastDrawerRenderAt&&now-lastMutationAt<2200&&now-lastDrawerRenderAt<2200);
    }catch{}
    return!1;
  }

  function shouldSuppressSelleasyUiRefresh(){
    try{
      var now=Date.now();
      var lastMutationAt=window.__vunroCartMutationAt||0;
      var lastDrawerRenderAt=window.__vunroLastDrawerRenderAt||0;
      var drawer=document.querySelector("cart-drawer");
      var drawerOpen=!!drawer&&(drawer.classList.contains("active")||drawer.hasAttribute("open"));
      var widgetPresent=!!(drawer&&drawer.querySelector("lb-upsell-widget"));
      return!!(drawerOpen&&widgetPresent&&lastMutationAt&&lastDrawerRenderAt&&now-lastMutationAt<2200&&now-lastDrawerRenderAt<2200);
    }catch{}
    return!1;
  }

  var originalFetch=window.fetch;
  if(typeof originalFetch==="function")window.fetch=async function(){
    var requestUrl=arguments[0];
    var urlString="";
    try{
      urlString=typeof requestUrl==="string"?requestUrl:String(requestUrl&&requestUrl.url||"");
    }catch{}
    try{
      if(urlString&&(/\/cart(\/|$)|\?sections=/.test(urlString))){
        window.__vunroCartTrace&&window.__vunroCartTrace.push("fetch-start",{url:urlString,method:(arguments[1]&&arguments[1].method)||"GET"});
      }
    }catch{}
    if(shouldSuppressSelleasyCartUpdate(urlString)){
      try{
        window.__vunroCartTrace&&window.__vunroCartTrace.push("selleasy-cart-update-suppressed",{url:urlString});
      }catch{}
      var cartRoot=((window.Shopify&&window.Shopify.routes&&window.Shopify.routes.root)||"/");
      var cartSnapshot=await originalFetch.call(this,cartRoot+"cart.js",{
        headers:{Accept:"application/json"}
      });
      return new Response(await cartSnapshot.text(),{
        status:200,
        headers:{"Content-Type":"application/json"}
      });
    }
    var response=await originalFetch.apply(this,arguments);
    try{
      if(urlString&&(/\/cart(\/|$)|\?sections=/.test(urlString))){
        window.__vunroCartTrace&&window.__vunroCartTrace.push("fetch-end",{url:urlString,status:response&&response.status});
      }
    }catch{}
    try{
      if(urlString&&urlString.indexOf("/cart/add")!==-1&&response&&response.ok){
        response.clone().json().then(function(payload){
          var productId=payload&&((payload.product_id||payload.productId)||((payload.items&&payload.items[0]&&payload.items[0].product_id)||""));
          if(!productId)return;
          window.__vunroLastAddedProductId=String(productId);
          try{
            window.__vunroCartTrace&&window.__vunroCartTrace.push("cart-add-product-context",{productId:String(productId)});
          }catch{}
        }).catch(function(){});
      }
    }catch{}
    markMutation(urlString);
    scheduleDrawerOpen(urlString,response);
    return response;
  };

  function patchDrawerRefresh(){
    var utils=window.lbSelleasyUtils;
    if(!utils||typeof utils.drawerRefresh!=="function")return!1;
    if(utils.__vunroLandingDrawerRefreshPatched)return!0;
    var originalDrawerRefresh=utils.drawerRefresh;
    utils.__vunroLandingDrawerRefreshPatched=!0;
    utils.drawerRefresh=function(){
      try{
        window.__vunroCartTrace&&window.__vunroCartTrace.push("selleasy-drawer-refresh",{});
      }catch{}
      var now=Date.now();
      var lastMutationAt=window.__vunroCartMutationAt||0;
      var lastRefreshAt=window.__vunroLandingLastSelleasyRefreshAt||0;
      if(lastMutationAt&&now-lastMutationAt<2200)return;
      if(lastRefreshAt&&now-lastRefreshAt<1400)return;
      window.__vunroLandingLastSelleasyRefreshAt=now;
      return originalDrawerRefresh.apply(this,arguments);
    };
    return!0;
  }

  function patchRenderHelper(){
    var utils=window.lbSelleasyUtils;
    if(!utils||typeof utils.renderHelper!=="function")return!1;
    if(utils.__vunroLandingRenderHelperPatched)return!0;
    var originalRenderHelper=utils.renderHelper;
    utils.__vunroLandingRenderHelperPatched=!0;
    utils.renderHelper=function(){
      if(shouldSuppressSelleasyUiRefresh()){
        try{
          window.__vunroCartTrace&&window.__vunroCartTrace.push("selleasy-render-helper-suppressed",{});
        }catch{}
        return;
      }
      return originalRenderHelper.apply(this,arguments);
    };
    return!0;
  }

  function patchOfferHandler(){
    var handlers=window.lbHandlers;
    if(!handlers||typeof handlers.offer!=="function")return!1;
    if(handlers.__vunroLandingOfferPatched)return!0;
    var originalOffer=handlers.offer;
    handlers.__vunroLandingOfferPatched=!0;
    handlers.offer=function(){
      var targetWidgets=Array.isArray(arguments[2])?arguments[2]:[];
      var touchesCartOffers=targetWidgets.some(function(widgetId){
        return widgetId==="widget#icuc"||widgetId==="widget#ccpa"||widgetId==="widget#ccpf";
      });
      if(touchesCartOffers&&shouldSuppressSelleasyUiRefresh()){
        try{
          window.__vunroCartTrace&&window.__vunroCartTrace.push("selleasy-offer-suppressed",{widgets:targetWidgets.slice(0,6)});
        }catch{}
        return;
      }
      return originalOffer.apply(this,arguments);
    };
    return!0;
  }

  var attempts=0;
  var timer=setInterval(function(){
    attempts+=1;
    var drawerRefreshReady=patchDrawerRefresh();
    var renderHelperReady=patchRenderHelper();
    var offerReady=patchOfferHandler();
    if((drawerRefreshReady&&renderHelperReady&&offerReady)||attempts>200)clearInterval(timer);
  },50);
})();

(function(){
  if(window.__vunroEmbeddedIframeCartBridge)return;
  if(document.querySelector(".vunro-blue-steel-native-host"))return;
  window.__vunroEmbeddedIframeCartBridge=!0;
  var allowedOrigin="https://integrations.getvunro.com";
  var state={busy:!1,lastKey:"",lastAt:0};

  function getCartSurface(){
    return document.querySelector("cart-drawer")||document.querySelector("cart-notification");
  }

  function centsToValue(rawAmount){
    var numeric=Number(rawAmount);
    if(!Number.isFinite(numeric))return void 0;
    return Math.round(numeric)/100;
  }

  function resolveCurrency(cartState){
    if(cartState&&typeof cartState.currency==="string"&&cartState.currency.trim())return cartState.currency.trim().toUpperCase();
    if(window.Shopify&&window.Shopify.currency&&typeof window.Shopify.currency.active==="string"&&window.Shopify.currency.active.trim())return window.Shopify.currency.active.trim().toUpperCase();
    return void 0;
  }

  function getCartItems(cartState){
    if(cartState&&Array.isArray(cartState.items))return cartState.items.filter(function(item){return item&&typeof item==="object";});
    if(cartState&&typeof cartState==="object"&&(cartState.variant_id||cartState.id))return[cartState];
    return[];
  }

  function findCartItemByVariant(cartState,variantId){
    var numericVariantId=Number(variantId);
    if(!Number.isFinite(numericVariantId)||numericVariantId<=0)return null;
    var items=getCartItems(cartState);
    for(var index=0;index<items.length;index+=1){
      var item=items[index];
      var itemVariantId=Number(item.variant_id||item.id);
      if(Number.isFinite(itemVariantId)&&itemVariantId===numericVariantId)return item;
    }
    return null;
  }

  function buildMetaContents(items){
    return items.map(function(item){
      var quantity=Number(item.quantity||1);
      if(!Number.isFinite(quantity)||quantity<=0)quantity=1;
      var itemPrice=item.final_price;
      if(!Number.isFinite(Number(itemPrice)))itemPrice=item.price;
      return{
        id:String(item.variant_id||item.id||""),
        quantity:quantity,
        item_price:centsToValue(itemPrice)
      };
    }).filter(function(item){
      return item.id;
    });
  }

  function buildAddToCartPayload(cartState,variantId,quantity){
    var trackedItem=findCartItemByVariant(cartState,variantId);
    var trackedQuantity=Number(quantity||1);
    if(!Number.isFinite(trackedQuantity)||trackedQuantity<=0)trackedQuantity=1;
    var contents=trackedItem?buildMetaContents([trackedItem]):[{id:String(variantId),quantity:trackedQuantity}];
    var payload={
      content_type:"product",
      content_ids:contents.map(function(item){return item.id;}),
      contents:contents
    };
    var currency=resolveCurrency(cartState);
    if(currency)payload.currency=currency;
    if(trackedItem){
      var trackedValue=Number(trackedItem.final_line_price);
      if(!Number.isFinite(trackedValue))trackedValue=Number(trackedItem.line_price);
      if(!Number.isFinite(trackedValue)){
        var trackedItemPrice=Number(trackedItem.final_price);
        if(!Number.isFinite(trackedItemPrice))trackedItemPrice=Number(trackedItem.price);
        if(Number.isFinite(trackedItemPrice))trackedValue=trackedItemPrice*trackedQuantity;
      }
      var convertedValue=centsToValue(trackedValue);
      if(typeof convertedValue==="number")payload.value=convertedValue;
    }
    return payload;
  }

  function buildInitiateCheckoutPayload(cartState){
    var items=getCartItems(cartState);
    if(!items.length)return null;
    var payload={
      content_type:"product",
      content_ids:items.map(function(item){return String(item.variant_id||item.id||"");}).filter(Boolean),
      contents:buildMetaContents(items)
    };
    var currency=resolveCurrency(cartState);
    if(currency)payload.currency=currency;
    var totalValue=centsToValue(cartState&&cartState.total_price);
    if(typeof totalValue==="number")payload.value=totalValue;
    if(Number.isFinite(Number(cartState&&cartState.item_count)))payload.num_items=Number(cartState.item_count);
    return payload;
  }

  function normalizeMetaEventArgs(argsLike){
    var args=Array.isArray(argsLike)?argsLike.slice():Array.prototype.slice.call(argsLike||[]);
    var method=typeof args[0]==="string"?args[0]:"";
    if(!method)return null;
    if(method==="trackSingle"){
      return {
        method:method,
        eventName:typeof args[2]==="string"?args[2]:"",
        payload:args[3]&&typeof args[3]==="object"?args[3]:{},
        options:args[4]&&typeof args[4]==="object"?args[4]:{}
      };
    }
    if(method==="track"||method==="trackCustom"){
      return {
        method:method,
        eventName:typeof args[1]==="string"?args[1]:"",
        payload:args[2]&&typeof args[2]==="object"?args[2]:{},
        options:args[3]&&typeof args[3]==="object"?args[3]:{}
      };
    }
    return null;
  }

  function metaEventSignature(eventName,payload){
    var state=payload&&typeof payload==="object"?payload:{};
    var ids=Array.isArray(state.content_ids)
      ?state.content_ids.map(function(value){return String(value||"");}).filter(Boolean).sort().join(",")
      :"";
    var contents=Array.isArray(state.contents)?state.contents.length:0;
    var value=typeof state.value==="number"?state.value.toFixed(2):"";
    var currency=typeof state.currency==="string"?state.currency:"";
    var numItems=Number.isFinite(Number(state.num_items))?String(Number(state.num_items)):"";
    return [String(eventName||""),ids,String(contents),value,currency,numItems].join("|");
  }

  function rememberObservedMetaEvent(eventName,payload,observedAt){
    if(!eventName)return;
    var store=window.__vunroObservedMetaEvents||(window.__vunroObservedMetaEvents={});
    var eventStore=store[eventName]||(store[eventName]=[]);
    var now=Number(observedAt)||Date.now();
    var signature=metaEventSignature(eventName,payload);
    var nextEntries=eventStore.filter(function(entry){
      return entry&&now-Number(entry.ts||0)<5000;
    });
    nextEntries.push({signature:signature,ts:now});
    store[eventName]=nextEntries;
  }

  function hasRecentObservedMetaEvent(eventName,payload,thresholdMs){
    if(!eventName)return!1;
    var store=window.__vunroObservedMetaEvents||{};
    var eventStore=Array.isArray(store[eventName])?store[eventName]:[];
    var now=Date.now();
    var threshold=Number(thresholdMs);
    if(!Number.isFinite(threshold)||threshold<=0)threshold=2000;
    var signature=metaEventSignature(eventName,payload);
    return eventStore.some(function(entry){
      return entry&&entry.signature===signature&&now-Number(entry.ts||0)<threshold;
    });
  }

  function installMetaObserver(){
    if(window.__vunroMetaObserverInstalled||typeof window.fbq!=="function")return;
    var originalFbq=window.fbq;
    var observedFbq=function(){
      var normalized=normalizeMetaEventArgs(arguments);
      if(normalized&&normalized.eventName){
        rememberObservedMetaEvent(normalized.eventName,normalized.payload,Date.now());
      }
      return originalFbq.apply(this,arguments);
    };
    for(var key in originalFbq){
      if(Object.prototype.hasOwnProperty.call(originalFbq,key)){
        observedFbq[key]=originalFbq[key];
      }
    }
    observedFbq.callMethod=originalFbq.callMethod;
    observedFbq.queue=originalFbq.queue;
    observedFbq.loaded=originalFbq.loaded;
    observedFbq.version=originalFbq.version;
    observedFbq.push=function(){
      return observedFbq.apply(this,arguments);
    };
    window.fbq=observedFbq;
    if(window._fbq===originalFbq)window._fbq=observedFbq;
    window.__vunroMetaObserverInstalled=!0;
  }

  function trackMetaEvent(eventName,payload,dedupeKey,ttlMs){
    if(typeof window.fbq!=="function"||!eventName)return!1;
    installMetaObserver();
    var now=Date.now();
    var bucket=window.__vunroMetaEventLog||(window.__vunroMetaEventLog={});
    var key=String(dedupeKey||eventName);
    var thresholdMs=Number(ttlMs);
    if(!Number.isFinite(thresholdMs)||thresholdMs<=0)thresholdMs=2000;
    if(bucket[key]&&now-bucket[key]<thresholdMs)return!1;
    if(hasRecentObservedMetaEvent(eventName,payload,thresholdMs))return!1;
    bucket[key]=now;
    window.fbq("track",eventName,payload||{});
    document.dispatchEvent(new CustomEvent("vunro:meta-track",{detail:{eventName:eventName,payload:payload||{},dedupeKey:key}}));
    return!0;
  }

  async function fetchCartStateSnapshot(){
    var cartUrl=(window.routes&&window.routes.cart_url)||"/cart";
    var normalizedCartUrl=typeof cartUrl==="string"&&cartUrl.trim()?cartUrl.trim():"/cart";
    if(!normalizedCartUrl.endsWith(".js"))normalizedCartUrl=normalizedCartUrl.replace(/\/$/,"")+".js";
    var response=await fetch(normalizedCartUrl,{
      method:"GET",
      headers:{"X-Requested-With":"XMLHttpRequest",Accept:"application/json"}
    });
    if(!response.ok)throw new Error("Embedded cart snapshot fetch failed with status "+response.status);
    return response.json();
  }

  function isCheckoutTrigger(node){
    if(!node||node.nodeType!==1)return!1;
    if(node.matches('a[href*="/checkout"]'))return!0;
    if(node.matches('button[name="checkout"], input[name="checkout"]'))return!0;
    if(node.matches("button[data-checkout],[data-vunro-checkout]"))return!0;
    var form=node.closest('form[action*="/cart"]');
    if(form&&(node.matches('button, input[type="submit"], input[type="image"]'))){
      var controlName=typeof node.getAttribute==="function"?String(node.getAttribute("name")||"").toLowerCase():"";
      var controlValue=typeof node.getAttribute==="function"?String(node.getAttribute("value")||"").toLowerCase():"";
      var controlText=String(node.textContent||"").toLowerCase();
      if(controlName==="checkout"||controlValue.indexOf("checkout")!==-1||controlText.indexOf("checkout")!==-1)return!0;
    }
    return!1;
  }

  async function trackInitiateCheckout(sourceLabel){
    try{
      if(typeof window.__vunroIsEmbeddedLandingPage!=="function"||!window.__vunroIsEmbeddedLandingPage())return!1;
      var cartState=await fetchCartStateSnapshot();
      var payload=buildInitiateCheckoutPayload(cartState);
      if(!payload||!payload.content_ids||!payload.content_ids.length)return!1;
      return trackMetaEvent("InitiateCheckout",payload,"embedded-initiate-checkout:"+String(sourceLabel||"unknown"),1500);
    }catch(error){
      console.warn("VUNRO embedded checkout tracking failed:",error);
      return!1;
    }
  }

  async function addVariantToCart(variantId,quantity){
    var numericId=Number(variantId);
    var numericQuantity=Number(quantity||1);
    if(!Number.isFinite(numericId)||numericId<=0)return;
    if(!Number.isFinite(numericQuantity)||numericQuantity<=0)numericQuantity=1;

    var cartSurface=getCartSurface();
    if(!cartSurface||typeof cartSurface.renderContents!=="function")return;

    if(typeof cartSurface.setActiveElement==="function"){
      cartSurface.setActiveElement(document.activeElement);
    }

    var formData=new FormData();
    var sectionsToRender=getRobustCartSectionIds(cartSurface);
    formData.append("id",String(numericId));
    formData.append("quantity",String(numericQuantity));

    sectionsToRender.forEach(function(sectionId){
      formData.append("sections",sectionId);
    });

    formData.append("sections_url",window.location.pathname);

    var response=await fetch((window.routes&&window.routes.cart_add_url)||"/cart/add",{
      method:"POST",
      headers:{"X-Requested-With":"XMLHttpRequest",Accept:"application/javascript"},
      body:formData
    });

    if(!response.ok)throw new Error("Embedded cart add failed with status "+response.status);

    var cartState=await response.json();
    if(!cartState||cartState.status)throw new Error("Embedded cart add returned an error payload");
    cartState=await ensureRenderableCartState(cartSurface,Object.assign({},cartState||{},{
      sections:cartState&&cartState.sections&&typeof cartState.sections==="object"?cartState.sections:{}
    }));

    cartSurface.classList.remove("is-empty");
    try{
      cartSurface.renderContents(cartState);
    }catch(renderError){
      if(!sectionsToRender.length)throw renderError;
      cartState=Object.assign({},cartState,{
        sections:await fetchCartSections(sectionsToRender)
      });
      cartSurface.renderContents(cartState);
    }
    trackMetaEvent("AddToCart",buildAddToCartPayload(cartState,numericId,numericQuantity),"embedded-add-to-cart:"+numericId,1500);
    if(typeof cartSurface.open==="function"&&!cartSurface.classList.contains("active")){
      cartSurface.open();
    }
    return cartState;
  }

  async function handleMessage(event){
    if(event.origin!==allowedOrigin||!event.data||(event.data.type!=="vunro:add-to-cart"&&event.data.type!=="vunro:add-to-cart-v2"))return;
    if(typeof event.stopImmediatePropagation==="function")event.stopImmediatePropagation();
    if(event.data.type==="vunro:add-to-cart-v2"){
      try{
        event.source&&typeof event.source.postMessage==="function"&&event.data.requestId&&event.source.postMessage({type:"vunro:add-to-cart-ack",requestId:event.data.requestId},event.origin);
      }catch(ackError){}
    }
    var key=String(event.data.variantId||"")+":"+String(event.data.quantity||1);
    var now=Date.now();
    if(state.busy)return;
    if(state.lastKey===key&&now-state.lastAt<1000)return;
    state.busy=!0;
    state.lastKey=key;
    state.lastAt=now;
    try{
      await addVariantToCart(event.data.variantId,event.data.quantity);
    }catch(error){
      console.error("VUNRO embedded cart bridge failed:",error);
    }finally{
      setTimeout(function(){state.busy=!1;},250);
    }
  }

  window.addEventListener("message",function(event){
    void handleMessage(event);
  },true);

  document.addEventListener("click",function(event){
    var target=event.target&&typeof event.target.closest==="function"
      ?event.target.closest('a[href*="/checkout"], button, input[type="submit"], input[type="image"], [data-checkout], [data-vunro-checkout]')
      :null;
    if(!isCheckoutTrigger(target))return;
    void trackInitiateCheckout("click");
  },true);

  document.addEventListener("submit",function(event){
    var form=event.target;
    if(!form||!form.matches||!form.matches('form[action*="/cart"]'))return;
    void trackInitiateCheckout("submit");
  },true);
})();
